/**
 * US date helpers. The app is US-only, so dates are always shown and typed as
 * MM/DD/YYYY while being stored as ISO `YYYY-MM-DD`.
 *
 * A native `<input type="date">` renders in the *browser's* locale, so the same
 * insurance expiry could read 08/01/2026 on one machine and 01/08/2026 on
 * another — which is how a policy date became ambiguous.
 */

/** ISO `YYYY-MM-DD` (or an ISO timestamp) → `MM/DD/YYYY`. */
export function isoToUsDate(iso) {
  if (!iso) return '';
  const m = String(iso).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(iso);
  const [, y, mo, d] = m;
  return `${mo}/${d}/${y}`;
}

/** `MM/DD/YYYY` → ISO `YYYY-MM-DD`. Returns '' when incomplete or invalid. */
export function usDateToIso(text) {
  const m = String(text || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return '';
  const [, mo, d, y] = m;
  const month = Number(mo);
  const day = Number(d);
  if (month < 1 || month > 12 || day < 1 || day > 31) return '';
  // `YYYY-MM-DDT00:00:00` (no Z) is parsed in LOCAL time, so the calendar
  // check has to read local parts too. Reading the UTC parts rejected every
  // date typed by anyone east of UTC (our Karachi / Delhi desks), which made
  // the field snap back to whatever it held before.
  const probe = new Date(`${y}-${mo}-${d}T00:00:00`);
  if (probe.getMonth() + 1 !== month || probe.getDate() !== day) return '';
  return `${y}-${mo}-${d}`;
}

/** Progressive mask: raw keystrokes → `MM/DD/YYYY` as the user types. */
export function maskUsDate(input) {
  const digits = String(input || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

/**
 * Today as ISO `YYYY-MM-DD` in the user's own timezone. `toISOString()` is UTC,
 * so it hands back yesterday's date to a Karachi desk working past 5am and
 * tomorrow's to a New York desk working past 7pm.
 */
export function todayIso() {
  const now = new Date();
  const mo = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${mo}-${d}`;
}

/** US Eastern — the company's operating timezone; handles EST/EDT itself. */
export const EASTERN_TZ = 'America/New_York';

/**
 * Parse a timestamp coming off our API into a Date, defensively.
 *
 * The backend writes `datetime.now(timezone.utc).isoformat()`, i.e.
 * `2026-02-26T18:00:14.995034+00:00` — six fractional digits. Safari's Date
 * parser rejects that outright, which is why every activity timeline read
 * "Invalid Date" on iPhone while Chrome on the desktop looked fine. Also
 * handles a space separator and a naive timestamp (treated as UTC, which is
 * what the backend stores).
 */
export function parseApiDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  let s = String(value).trim();
  if (!s) return null;
  s = s.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');
  // Safari also chokes on a colon-less offset (+0000).
  s = s.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  if (!/(Z|[+-]\d{2}:\d{2})$/.test(s) && /\d{2}:\d{2}/.test(s)) s += 'Z';
  const parsed = new Date(s);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** `Oct 6, 2026, 12:53 PM ET` — always US Eastern, whoever is looking. */
export function formatEastern(value, { fallback = '—', showTz = true } = {}) {
  const d = parseApiDate(value);
  if (!d) return fallback;
  const text = d.toLocaleString('en-US', {
    timeZone: EASTERN_TZ,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
  return showTz ? `${text} ET` : text;
}

/** `Oct 6, 2026` in US Eastern, no clock time. */
export function formatEasternDate(value, { fallback = '—' } = {}) {
  // A date-only string ("2026-10-19") — or a naive midnight stamp like
  // "2026-03-16T00:00:00", which is how older stop dates were stored — is a
  // calendar date, not an instant. Running it through a timezone shifts it a
  // day, which is how a March 16 pickup started reading March 15.
  const dateOnly = /^(\d{4}-\d{2}-\d{2})(?:[T ]00:00(?::00(?:\.0+)?)?)?$/.exec(String(value || '').trim());
  if (dateOnly) {
    const [y, m, d] = dateOnly[1].split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  }
  const d = parseApiDate(value);
  if (!d) return fallback;
  return d.toLocaleDateString('en-US', {
    timeZone: EASTERN_TZ, month: 'short', day: 'numeric', year: 'numeric',
  });
}
